/* :playback_observability - the fields every native playback payload carries.
 *
 * The Rails counterpart is Playback::ObservabilityContext, and the two must agree: videoId +
 * viewerId + subPropertyId are the keys native data would be joined to web's on, so a field
 * spelled differently here than it is in the JSON APIs is a field that silently stops joining.
 * Defined once rather than per bridge controller for the same reason - three copies is how
 * live, premiere and cold-start restore each ended up missing a different dimension.
 *
 * Every value is rendered server-side by Media::VideoPlayerComponent, and only when the flag is
 * on for the entity - so with the flag off observabilityValue is false, this returns {}, and the
 * payload native receives is byte-for-byte what it was before this existed.
 */

// Spread into a bridge controller's `static values`. All of these are rendered on the player
// element as data-media--video-player-* and renamed onto the bridge controller by
// maybeConnectBridge before connect() runs.
export const playbackObservabilityValues = {
  observability: Boolean,
  videoId: String,
  streamType: String,
  contentType: String,
  viewerId: String,
  subPropertyId: String,
  planTier: String,
  tokenType: String,
  expiresAtMs: Number,
  muxEnvKey: String
};

// Absent keys stay absent rather than being sent empty: an empty viewerId would merge every
// logged-out viewer into one fake person in Mux's viewer breakdowns, and an empty expiresAtMs
// would read as "expired" where the honest answer is "no advertised deadline".
//
// cdn and videoTitle are copied here but are NOT in playbackObservabilityValues above: only the
// on-demand video bridge declares them (Media::VideoPlayerComponent renders them for that
// surface). Everywhere else controller.cdnValue is undefined and the key stays absent, exactly
// like any other dimension a surface does not have. Both are Mux dimensions (video_cdn,
// video_title), so they belong behind this flag with the rest - sending them unconditionally
// from the on-demand bridge is what used to make the byte-for-byte claim above false there.
//
// One documented exception, which lives in the caller and not here: the LIVE bridge payload must
// always carry a numeric expiresAtMs. Live always has a signed IVS manifest and therefore always
// has a deadline, and Android builds already in the field decode the key as non-nullable. See
// media/bridge/live_player_controller.js. Live is the only consumer that requires presence; do
// not generalise that requirement into this function.
export function playbackObservabilityPayload(controller) {
  if (!controller.observabilityValue) return {};

  const payload = {};

  const copyString = (key) => {
    const value = controller[`${key}Value`];
    if (value) payload[key] = value;
  };

  [
    "videoId", "streamType", "contentType", "subPropertyId", "planTier", "tokenType",
    "muxEnvKey", "viewerId", "cdn", "videoTitle"
  ].forEach(copyString);

  if (controller.expiresAtMsValue) payload.expiresAtMs = controller.expiresAtMsValue;

  return payload;
}
